/**
 * 认证模块入口文件
 * 统一导出所有认证相关功能
 */

import { ref, computed, reactive } from 'vue'
import type { AuthState } from './types'
import { authManager } from './auth-manager'
import { permissionManager } from './permission-manager'

// 导出类型定义
export * from './types'

// 导出存储工具
export { storage, STORAGE_KEYS } from './storage'
export type { StorageConfig, StorageType } from './storage'

// 导出权限管理器
export {
  PermissionManager,
  permissionManager,
  RequirePermission,
  RequireRole,
  PERMISSION_FORMAT_REGEX
} from './permission-manager'

// 导出认证管理器
export { AuthManager, authManager } from './auth-manager'

// 全局权限状态
const authState = reactive<AuthState>({
  user: null,
  token: null,
  permissions: [],
  roles: [],
  isAuthenticated: false,
  loading: false,
  error: null
})

/**
 * 权限验证函数
 */
export function hasPermission(permission: string | string[], mode: 'all' | 'any' = 'all'): boolean {
  const permissions = Array.isArray(permission) ? permission : [permission]
  return permissionManager.hasPermissions(permissions, { mode })
}

/**
 * 角色验证函数
 */
export function hasRole(role: string | string[], mode: 'all' | 'any' = 'all'): boolean {
  const roles = Array.isArray(role) ? role : [role]
  return permissionManager.hasRoles(roles, mode)
}

/**
 * 检查是否为超级管理员
 */
export function isSuperAdmin(): boolean {
  return permissionManager.isSuperAdmin()
}

/**
 * 权限指令对象
 */
export const authDirective = {
  /**
   * 权限指令
   */
  permission: {
    mounted(el: HTMLElement, binding: any) {
      const { value, modifiers } = binding
      const mode = modifiers.any ? 'any' : 'all'

      if (!hasPermission(value, mode)) {
        el.style.display = 'none'
      }
    },
    updated(el: HTMLElement, binding: any) {
      const { value, modifiers } = binding
      const mode = modifiers.any ? 'any' : 'all'

      if (!hasPermission(value, mode)) {
        el.style.display = 'none'
      } else {
        el.style.display = ''
      }
    }
  },

  /**
   * 角色指令
   */
  role: {
    mounted(el: HTMLElement, binding: any) {
      const { value, modifiers } = binding
      const mode = modifiers.any ? 'any' : 'all'

      if (!hasRole(value, mode)) {
        el.style.display = 'none'
      }
    },
    updated(el: HTMLElement, binding: any) {
      const { value, modifiers } = binding
      const mode = modifiers.any ? 'any' : 'all'

      if (!hasRole(value, mode)) {
        el.style.display = 'none'
      } else {
        el.style.display = ''
      }
    }
  }
}

/**
 * 认证组合式API
 */
export function useAuth() {
  // 响应式状态
  const isAuthenticated = computed(() => authManager.isAuthenticated())
  const currentUser = computed(() => authManager.getCurrentUser())
  const currentToken = computed(() => authManager.getCurrentToken())
  const userPermissions = computed(() => permissionManager.getUserPermissions())
  const userRoles = computed(() => permissionManager.getUserRoles())
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * 登录
   */
  const login = async (credentials: any) => {
    loading.value = true
    error.value = null

    try {
      const response = await authManager.login(credentials)
      // 更新响应式状态
      Object.assign(authState, authManager.getAuthState())
      return response
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 注意：logout方法已移除，统一使用 stores/user.ts 中的 logout 方法

  /**
   * 刷新token
   */
  const refreshToken = async () => {
    try {
      const newToken = await authManager.refreshToken()
      if (newToken) {
        // 更新响应式状态
        Object.assign(authState, authManager.getAuthState())
      }
      return newToken
    } catch (err: any) {
      error.value = err.message
      throw err
    }
  }

  /**
   * 检查权限
   */
  const checkPermission = (permission: string | string[], mode: 'all' | 'any' = 'all') => {
    return hasPermission(permission, mode)
  }

  /**
   * 检查角色
   */
  const checkRole = (role: string | string[], mode: 'all' | 'any' = 'all') => {
    return hasRole(role, mode)
  }

  return {
    // 状态
    isAuthenticated,
    currentUser,
    currentToken,
    userPermissions,
    userRoles,
    loading,
    error,

    // 方法
    login,
    // logout 方法已移除，请使用 stores/user.ts 中的 logout 方法
    refreshToken,
    checkPermission,
    checkRole,
    hasPermission: checkPermission,
    hasRole: checkRole,
    isSuperAdmin
  }
}

/**
 * 初始化认证模块
 */
export function initAuth(config: any = {}, apiClient?: any) {
  // 初始化认证管理器
  authManager.initialize(config, apiClient)

  // 更新响应式状态
  Object.assign(authState, authManager.getAuthState())

  return {
    authManager,
    permissionManager,
    authState
  }
}

// 创建全局实例别名（兼容原有代码）
export const authManagerInstance = authManager

// 导出auth对象（兼容原有代码）
export const auth = {
  authManager,
  permissionManager,
  hasPermission,
  hasRole,
  isSuperAdmin,
  useAuth,
  initAuth
}
